import React, { useState, useEffect, useCallback, useRef } from 'react';
import { OrderList } from '../components/orders/OrderList';
import { OrderDetailsDrawer } from '../components/orders/OrderDetailsDrawer';
import { OrderImportModal } from '../components/orders/OrderImportModal';
import { OrdersSectionNav } from '../components/orders/OrdersSectionNav';
import { listOrders, getOrder, getOrderLineItems, estimateIncompleteCogs, Order, OrderLineItem } from '../api/orders';
import { markManapoolItemUnfound, markManapoolOrderReplaced, retryManapoolItemAllocation, fetchManapoolOrderReports, applyManapoolOrderReports } from '../api/manapoolOrders';
import { notifyPendingOrdersChanged } from '../hooks/usePendingOrders';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../styles/tokens';
import { ensureApiAuthenticated } from '../services/api';
import {
  fulfillmentStatusLabel,
  fulfillmentStatusesForGroup,
  PENDING_FULFILLMENT_STATUSES,
} from '../utils/orderStatus';

interface OrdersPageProps {
  fixedSource?: 'all' | 'manapool' | 'shopify' | 'ebay' | 'tcgplayer';
  title?: string;
  subtitle?: string;
  cardTitle?: string;
  initialStatusGroup?: 'pending' | 'completed';
}

type SourceFilter = 'all' | 'manapool' | 'shopify' | 'ebay' | 'tcgplayer';

const OrdersPage: React.FC<OrdersPageProps> = ({
  fixedSource,
  title = 'Orders',
  subtitle = 'Combined view of all orders across sales channels',
  cardTitle = 'Combined Orders',
  initialStatusGroup = 'pending',
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [statusGroup, setStatusGroup] = useState<'pending' | 'completed'>(initialStatusGroup);
  const [source, setSource] = useState<SourceFilter>(fixedSource ?? 'all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [fulfillmentStatus, setFulfillmentStatus] = useState('all');

  // Drawer state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedLineItems, setSelectedLineItems] = useState<OrderLineItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoadingLineItems, setIsLoadingLineItems] = useState(false);

  // Import modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const drawerRequest = useRef(0);

  useEffect(() => {
    if (fixedSource) {
      setSource(fixedSource);
      setPage(1);
    }
  }, [fixedSource]);

  useEffect(() => {
    setStatusGroup(initialStatusGroup);
  }, [initialStatusGroup]);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await ensureApiAuthenticated();
      const params: any = { page, page_size: limit };
      params.status_group = statusGroup;
      if (source !== 'all') params.source = source;
      if (fulfillmentStatus !== 'all') params.fulfillment_status = fulfillmentStatus;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (searchQuery) params.search = searchQuery;
      const response = await listOrders(params);
      setOrders(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
      setOrders([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [statusGroup, source, fulfillmentStatus, startDate, endDate, page, limit, searchQuery]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field: 'source' | 'startDate' | 'endDate' | 'fulfillmentStatus', value: string) => {
    if (field === 'source' && !fixedSource) setSource(value as SourceFilter);
    if (field === 'startDate') setStartDate(value);
    if (field === 'endDate') setEndDate(value);
    if (field === 'fulfillmentStatus') setFulfillmentStatus(value);
    setPage(1);
  };

  const handleStatusGroup = (group: 'pending' | 'completed') => {
    setStatusGroup(group);
    setFulfillmentStatus('all');
    setPage(1);
  };

  const handleClearFilters = () => {
    setSource(fixedSource ?? 'all');
    setFulfillmentStatus('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleOrderClick = async (order: Order) => {
    const requestId = ++drawerRequest.current;
    setSelectedOrder(order);
    setIsDrawerOpen(true);
    setIsLoadingLineItems(true);
    try {
      await ensureApiAuthenticated();
      const lineItems = await getOrderLineItems(order.id);
      if (requestId !== drawerRequest.current) return;
      setSelectedLineItems(lineItems);
    } catch (err) {
      if (requestId !== drawerRequest.current) return;
      console.error('Failed to load line items:', err);
      setSelectedLineItems([]);
    } finally {
      if (requestId === drawerRequest.current) {
        setIsLoadingLineItems(false);
      }
    }
  };

  const handleCloseDrawer = () => {
    drawerRequest.current += 1;
    setIsDrawerOpen(false);
    setSelectedOrder(null);
    setSelectedLineItems([]);
  };

  const handleOrderUpdated = useCallback((updated: Order) => {
    setSelectedOrder((current) => (
      current && current.id === updated.id ? { ...current, ...updated } : current
    ));
    setOrders((prev) => prev.map((row) => (
      row.id === updated.id ? { ...row, ...updated } : row
    )));
  }, []);

  const handleOpenPartner = useCallback(async (partnerOrderId: string) => {
    const requestId = ++drawerRequest.current;
    setIsDrawerOpen(true);
    setIsLoadingLineItems(true);
    try {
      await ensureApiAuthenticated();
      const [partner, lineItems] = await Promise.all([
        getOrder(partnerOrderId),
        getOrderLineItems(partnerOrderId),
      ]);
      if (requestId !== drawerRequest.current) return;
      setSelectedOrder(partner);
      setSelectedLineItems(lineItems);
    } catch (err) {
      if (requestId !== drawerRequest.current) return;
      setError(err instanceof Error ? err.message : 'Failed to open partner order');
    } finally {
      if (requestId === drawerRequest.current) {
        setIsLoadingLineItems(false);
      }
    }
  }, []);

  const loadSelectedOrderLineItems = useCallback(async () => {
    if (!selectedOrder) {
      return;
    }
    setIsLoadingLineItems(true);
    try {
      await ensureApiAuthenticated();
      const lineItems = await getOrderLineItems(selectedOrder.id);
      setSelectedLineItems(lineItems);
    } catch (err) {
      console.error('Failed to refresh line items:', err);
    } finally {
      setIsLoadingLineItems(false);
    }
  }, [selectedOrder]);

  const handleMarkMissing = useCallback(async (itemId: string, quantityFound: number, notes?: string) => {
    if (!selectedOrder || selectedOrder.source.toLowerCase() !== 'manapool') {
      throw new Error('Mark Missing is currently supported for Manapool orders only.');
    }
    await ensureApiAuthenticated();
    await markManapoolItemUnfound(selectedOrder.external_id, itemId, quantityFound, notes);
    await fetchOrders();
    await loadSelectedOrderLineItems();
    notifyPendingOrdersChanged();
  }, [selectedOrder, fetchOrders, loadSelectedOrderLineItems]);

  const handleRetryShortfall = useCallback(async (itemId: string) => {
    if (!selectedOrder || selectedOrder.source.toLowerCase() !== 'manapool') {
      throw new Error('Retry shortfall is currently supported for Manapool orders only.');
    }
    await ensureApiAuthenticated();
    await retryManapoolItemAllocation(selectedOrder.external_id, itemId);
    await fetchOrders();
    await loadSelectedOrderLineItems();
    notifyPendingOrdersChanged();
  }, [selectedOrder, fetchOrders, loadSelectedOrderLineItems]);

  const handleMarkReplaced = useCallback(async () => {
    if (!selectedOrder || selectedOrder.source.toLowerCase() !== 'manapool') {
      throw new Error('Mark Replaced is currently supported for Manapool orders only.');
    }
    await ensureApiAuthenticated();
    await markManapoolOrderReplaced(selectedOrder.external_id);
    await fetchOrders();
    handleCloseDrawer();
    notifyPendingOrdersChanged();
  }, [selectedOrder, fetchOrders]);

  const handleFetchReports = useCallback(async () => {
    if (!selectedOrder || selectedOrder.source.toLowerCase() !== 'manapool') {
      throw new Error('Reports are currently supported for Manapool orders only.');
    }
    await ensureApiAuthenticated();
    return fetchManapoolOrderReports(selectedOrder.external_id);
  }, [selectedOrder]);

  const handleApplyReports = useCallback(async (payload: {
    report_id?: string | null;
    lines: Array<{ line_id: string; quantity: number; restock: boolean }>;
  }) => {
    if (!selectedOrder || selectedOrder.source.toLowerCase() !== 'manapool') {
      throw new Error('Reports are currently supported for Manapool orders only.');
    }
    await ensureApiAuthenticated();
    await applyManapoolOrderReports(selectedOrder.external_id, payload);
    await fetchOrders();
    await loadSelectedOrderLineItems();
    notifyPendingOrdersChanged();
  }, [selectedOrder, fetchOrders, loadSelectedOrderLineItems]);

  const totalPages = Math.ceil(total / limit);
  const hasFilters = (source !== (fixedSource ?? 'all')) || fulfillmentStatus !== 'all' || startDate !== '' || endDate !== '';
  const statusFilterOptions = fulfillmentStatusesForGroup(statusGroup);
  const showStart = (page - 1) * limit + 1;
  const showEnd = Math.min(page * limit, total);
  const statusLabel = statusGroup === 'pending' ? 'pending' : 'completed';
  const pendingStatuses = PENDING_FULFILLMENT_STATUSES as readonly string[];
  const canMarkMissing = Boolean(
    selectedOrder
      && statusGroup === 'pending'
      && selectedOrder.source.toLowerCase() === 'manapool'
      && pendingStatuses.includes(selectedOrder.fulfillment_status)
  );
  const canMarkReplaced = canMarkMissing;
  const canRetryShortfall = canMarkMissing;
  const canImportReports = Boolean(
    selectedOrder
      && selectedOrder.source.toLowerCase() === 'manapool'
      && (
        ['shipped', 'delivered'].includes(selectedOrder.fulfillment_status)
        || pendingStatuses.includes(selectedOrder.fulfillment_status)
      )
  );

  return (
    <>
      <div style={{ padding: spacing.lg }}>
        {/* Page header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing.xl,
        }}>
          <div>
            <h1 style={{
              fontSize: typography.h1.fontSize,
              fontWeight: typography.h1.fontWeight,
              color: colors.text,
              margin: 0,
              marginBottom: spacing.xs,
            }}>
              {title}
            </h1>
            <p style={{ fontSize: typography.body.fontSize, color: colors.textSecondary, margin: 0 }}>
              {subtitle}
            </p>
            <OrdersSectionNav />
          </div>
          <button
            onClick={() => setIsImportModalOpen(true)}
            style={{
              marginTop: spacing.xs,
              padding: `${spacing.sm} ${spacing.lg}`,
              backgroundColor: colors.primary,
              color: colors.white,
              border: 'none',
              borderRadius: borderRadius.md,
              fontSize: typography.body.fontSize,
              fontWeight: typographyFlat.fontWeight.medium,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}
          >
            <span>📥</span>
            Import Orders
          </button>
        </div>

        {/* Main card */}
        <div style={{
          backgroundColor: colors.white,
          border: `1px solid ${colors.border}`,
          borderRadius: borderRadius.lg,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${spacing.md} ${spacing.lg}`,
            borderBottom: `1px solid ${colors.border}`,
          }}>
            <div>
              <h2 style={{
                fontSize: typography.h2.fontSize,
                fontWeight: typography.h2.fontWeight,
                color: colors.text,
                margin: 0,
                marginBottom: spacing.xs,
              }}>
                {cardTitle}
              </h2>
              <div style={{
                display: 'inline-flex',
                gap: spacing.sm,
                borderBottom: `1px solid ${colors.border}`,
              }}>
                <button
                  onClick={() => handleStatusGroup('pending')}
                  style={{
                    padding: `${spacing.sm} ${spacing.md}`,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: statusGroup === 'pending' ? colors.primary : colors.textSecondary,
                    borderBottom: statusGroup === 'pending' ? `2px solid ${colors.primary}` : 'none',
                    fontSize: typography.body.fontSize,
                    fontWeight: typographyFlat.fontWeight.semibold,
                    cursor: 'pointer',
                    marginBottom: '-1px',
                  }}
                >
                  Pending
                </button>
                <button
                  onClick={() => handleStatusGroup('completed')}
                  style={{
                    padding: `${spacing.sm} ${spacing.md}`,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: statusGroup === 'completed' ? colors.primary : colors.textSecondary,
                    borderBottom: statusGroup === 'completed' ? `2px solid ${colors.primary}` : 'none',
                    fontSize: typography.body.fontSize,
                    fontWeight: typographyFlat.fontWeight.semibold,
                    cursor: 'pointer',
                    marginBottom: '-1px',
                  }}
                >
                  Completed
                </button>
              </div>
            </div>
            {total > 0 && (
              <span style={{ fontSize: typography.small.fontSize, color: colors.textSecondary }}>
                Total {statusLabel}: {total.toLocaleString()} • Showing {showStart}–{showEnd}
              </span>
            )}
          </div>

          {/* Toolbar */}
          <div style={{
            padding: `${spacing.md} ${spacing.lg}`,
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.sm,
          }}>
            {/* Row 1: Search + count */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search Order ID, buyer, or item"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{
                  width: '320px',
                  padding: `${spacing.xs} ${spacing.sm}`,
                  fontSize: typography.small.fontSize,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.md,
                  backgroundColor: colors.white,
                  color: colors.text,
                  outline: 'none',
                }}
              />
            </div>

            {/* Row 2: Inline filters */}
            <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                <label htmlFor="orders-fulfillment-filter" style={{ fontSize: typography.small.fontSize, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                  Status
                </label>
                <select
                  id="orders-fulfillment-filter"
                  aria-label="Status"
                  value={fulfillmentStatus}
                  onChange={(e) => handleFilterChange('fulfillmentStatus', e.target.value)}
                  disabled={isLoading}
                  style={{
                    padding: `${spacing.xs} ${spacing.sm}`,
                    fontSize: typography.small.fontSize,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.md,
                    backgroundColor: colors.white,
                    color: colors.text,
                    cursor: 'pointer',
                  }}
                >
                  <option value="all">All statuses</option>
                  {statusFilterOptions.map((status) => (
                    <option key={status} value={status}>
                      {fulfillmentStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>

              {!fixedSource && (
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                  <label style={{ fontSize: typography.small.fontSize, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                    Source
                  </label>
                  <select
                    value={source}
                    onChange={(e) => handleFilterChange('source', e.target.value)}
                    disabled={isLoading}
                    style={{
                      padding: `${spacing.xs} ${spacing.sm}`,
                      fontSize: typography.small.fontSize,
                      border: `1px solid ${colors.border}`,
                      borderRadius: borderRadius.md,
                      backgroundColor: colors.white,
                      color: colors.text,
                      cursor: 'pointer',
                    }}
                  >
                    <option value="all">All Sources</option>
                    <option value="tcgplayer">TCGplayer</option>
                    <option value="shopify">Shopify</option>
                    <option value="ebay">eBay</option>
                    <option value="manapool">Manapool</option>
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                <label style={{ fontSize: typography.small.fontSize, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                  From
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  disabled={isLoading}
                  style={{
                    padding: `${spacing.xs} ${spacing.sm}`,
                    fontSize: typography.small.fontSize,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.md,
                    backgroundColor: colors.white,
                    color: colors.text,
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                <label style={{ fontSize: typography.small.fontSize, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                  To
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  disabled={isLoading}
                  style={{
                    padding: `${spacing.xs} ${spacing.sm}`,
                    fontSize: typography.small.fontSize,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.md,
                    backgroundColor: colors.white,
                    color: colors.text,
                  }}
                />
              </div>

              {hasFilters && (
                <button
                  onClick={handleClearFilters}
                  disabled={isLoading}
                  style={{
                    padding: `${spacing.xs} ${spacing.sm}`,
                    backgroundColor: 'transparent',
                    color: colors.primary,
                    border: 'none',
                    borderRadius: borderRadius.sm,
                    fontSize: typography.small.fontSize,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              margin: spacing.md,
              padding: spacing.md,
              backgroundColor: colors.errorLight,
              border: `1px solid ${colors.error}`,
              borderRadius: borderRadius.md,
              color: colors.error,
              fontSize: typography.small.fontSize,
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Order table */}
          <OrderList orders={orders} isLoading={isLoading} onOrderClick={handleOrderClick} bare />

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: spacing.md,
              padding: spacing.md,
              borderTop: `1px solid ${colors.border}`,
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  backgroundColor: page === 1 ? colors.backgroundAlt : colors.white,
                  color: page === 1 ? colors.textSecondary : colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.md,
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: typography.small.fontSize,
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: typography.small.fontSize, color: colors.textSecondary }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  padding: `${spacing.xs} ${spacing.md}`,
                  backgroundColor: page === totalPages ? colors.backgroundAlt : colors.white,
                  color: page === totalPages ? colors.textSecondary : colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.md,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: typography.small.fontSize,
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <OrderDetailsDrawer
        order={selectedOrder}
        lineItems={selectedLineItems}
        isOpen={isDrawerOpen}
        isLoading={isLoadingLineItems}
        canMarkMissing={canMarkMissing}
        canMarkReplaced={canMarkReplaced}
        canRetryShortfall={canRetryShortfall}
        canImportReports={canImportReports}
        onMarkMissing={handleMarkMissing}
        onMarkReplaced={handleMarkReplaced}
        onRetryShortfall={handleRetryShortfall}
        onFetchReports={handleFetchReports}
        onApplyReports={handleApplyReports}
        onEstimateIncompleteCogs={
          selectedOrder
            ? async () => {
                if (selectedOrder.source.toLowerCase() === 'manapool') {
                  try {
                    await fetchManapoolOrderReports(selectedOrder.external_id);
                  } catch {
                    // Cached reports may still scope lines.
                  }
                }
                await estimateIncompleteCogs(selectedOrder.id, {
                  prefer_reports: true,
                  apply: true,
                });
                await loadSelectedOrderLineItems();
              }
            : undefined
        }
        onOrderUpdated={handleOrderUpdated}
        onOpenPartner={handleOpenPartner}
        onClose={handleCloseDrawer}
      />
      <OrderImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => { setIsImportModalOpen(false); fetchOrders(); }}
      />
    </>
  );
};

export default OrdersPage;
